import { useEffect } from "react";

/**
 * Tiny SEO hook — sets <title> and the description meta tag.
 * Restores previous values on unmount so SPA navigation between pages stays clean.
 * Keeps the Helmet dependency footprint at zero.
 */
function setMeta(name, content) {
    if (typeof document === "undefined") return null;
    let tag = document.querySelector(`meta[name="${name}"]`);
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
    }
    const previous = tag.getAttribute("content");
    tag.setAttribute("content", content);
    return previous;
}

export default function useSeo({ title, description, ogTitle, ogDescription }) {
    useEffect(() => {
        if (typeof document === "undefined") return undefined;
        const prevTitle = document.title;
        if (title) document.title = title;

        const restorers = [];
        if (description) {
            const prev = setMeta("description", description);
            restorers.push(() => prev !== null && setMeta("description", prev));
        }
        if (ogTitle || title) {
            const prev = setMeta("og:title", ogTitle || title);
            restorers.push(() => prev !== null && setMeta("og:title", prev));
        }
        if (ogDescription || description) {
            const prev = setMeta("og:description", ogDescription || description);
            restorers.push(() => prev !== null && setMeta("og:description", prev));
        }

        return () => {
            document.title = prevTitle;
            restorers.forEach((fn) => fn());
        };
    }, [title, description, ogTitle, ogDescription]);
}
